import { Primary } from '@decorators/entity-primary.decorator';
import { Required } from '@decorators/entity-required.decorator';
import { Entity } from './entity.class';

import { Rapport } from './rapport.class';
import { PhraseRapport } from './phrase-rapport.class';
import { QuestionRapport } from './question-rapport.class';
import { RapportInvestigation } from './rapport-investigation.class';
import { v4 as uuidv4 } from 'uuid';

export enum PhotoType {
	FACADE = 'facade',
	QUESTION = 'question',
	PHRASE = 'phrase',
	SUPPORT = 'support',
	OTHER = 'other',
	MANUAL = 'manual',
	NONE = 'none',
	/** Annexe cat nat : capture de carte satellite (puis sa version annotée). */
	ANNEXE = 'annexe',
}

/**
 * Réglages d'une annexe cat nat (colonne JSON `photo.carte`) :
 *  - source : `ign` (capture de la vue satellite) ou `plan` (image importée) ;
 *  - lat / lng / zoom : position de la carte (vue satellite uniquement) ;
 *  - angle : direction vers laquelle pointe le haut de l'image, en degrés depuis le nord,
 *    sens horaire ; `null` = nord inconnu, pas de boussole dans le PDF ;
 *  - legende : `false` pour ne pas imprimer la légende sous cette annexe ;
 *  - titre : titre de la page du PDF ; absent = titre par défaut de la légende.
 */
export interface PositionCarte {
	source?: 'ign' | 'plan';
	lat?: number;
	lng?: number;
	zoom?: number;
	angle: number | null;
	legende?: boolean;
	titre?: string;
}


export class Photo implements Entity {
	@Required
	@Primary
	id: string;

	@Required
	fileName: string;

	// Optional in IndexedDB to avoid iOS ModifyError when persisting Blobs
	// file?: File;

	@Required
	type: PhotoType;

	isSync: boolean;

	@Required
	rapport: Rapport;

	phraseRapport: PhraseRapport;
	phraseRapportId: string | null;
	question: QuestionRapport;
	questionId: string | null;
	supportId: string | null;
	investigation: RapportInvestigation;
	investigationId: string | null;
	condition: string | null;

	createdAt: Date;

	parent: Photo | null;
	parentId: string | null = null;

	// Suivi local des échecs d'envoi (persisté dans IndexedDB, lu par getBlockedPhotos /
	// unblockPhoto). Volontairement HORS keepProperties : jamais envoyé à l'API.
	uploadBlocked?: boolean;
	lastError?: string;
	lastAttempt?: number;
	snapshot: string = '';

	update: boolean = false;

	objectUrl?: string;    // runtime (object URL) non persisté
	/**
	 * Runtime, hors mode technicien : URL S3 de la photo ENTIÈRE, quand `objectUrl` porte
	 * la miniature 512 px. Réservée au zoom plein écran (voir PhotoUrlService).
	 */
	urlComplete?: string;
	/** Runtime : image introuvable après les tentatives de réparation (tuile « indisponible »). */
	_indisponible?: boolean;
	_runtimePrepared?: boolean;

	sort: number;
	insideSort: number;
	
	captureDate: Date | null;

	/** Annexe cat nat uniquement. Pas d'initialiseur : il serait envoyé à chaque patch (cf. parentId). */
	carte?: PositionCarte | null;

	// ========================================
	// NOUVEAU SYSTÈME WORKFLOW
	// ========================================

	workflowNodeId?: string;
	workflowOptionValue?: string;

	// ========================================

	constructor() {	
		if (!this.id) this.id = uuidv4();
	}

	// Used by DynamicService.removeForeignProperties to retain necessary fields
	static keepProperties = ['id','fileName','type','isSync','rapport','phraseRapportId','questionId','supportId','investigationId','condition','createdAt','parentId','snapshot','update','sort','insideSort','captureDate','workflowNodeId','workflowOptionValue','carte'];
}
